// src/constants/signals.ts
// Signal metadata and definitions for the Operation Center signal widgets

import type { WidgetType } from "@/types/dashboard";

// Placeholder run_id — replace with the actual run_id from your backend
export const SIGNAL_RUN_ID = "d8391d38-6495-4837-b974-d49c41a05ea5";

export interface SignalDefinition {
  signalName: string;
  widgetType: WidgetType;
  label: string;
  description: string;
}

export const SIGNAL_DEFINITIONS: SignalDefinition[] = [
  {
    signalName: "z_score",
    widgetType: "signal_zscore",
    label: "Z-Score",
    description: "Standardized deviation from mean",
  },
  {
    signalName: "hurst",
    widgetType: "signal_hurst",
    label: "Hurst Exponent",
    description: "Mean-reversion vs trending measure",
  },
  {
    signalName: "sig_correlation",
    widgetType: "signal_correlation",
    label: "Correlation",
    description: "Rolling correlation signal",
  },
  {
    signalName: "sig_vol_ratio_z",
    widgetType: "signal_vol_ratio_z",
    label: "Vol Ratio Z",
    description: "Volatility ratio z-score",
  },
  {
    signalName: "sig_funding_divergence",
    widgetType: "signal_funding_divergence",
    label: "Funding Divergence",
    description: "Funding rate divergence",
  },
  {
    signalName: "sig_carry_to_vol",
    widgetType: "signal_carry_to_vol",
    label: "Carry/Vol",
    description: "Carry trade to volatility ratio",
  },
  {
    signalName: "sig_funding_momentum",
    widgetType: "signal_funding_momentum",
    label: "Funding Momentum",
    description: "Funding rate momentum",
  },
  {
    signalName: "sig_funding_zscore",
    widgetType: "signal_funding_zscore",
    label: "Funding Z-Score",
    description: "Funding rate z-score",
  },
  {
    signalName: "sig_ratio_rsi",
    widgetType: "signal_ratio_rsi",
    label: "Ratio RSI",
    description: "Relative strength of price ratio",
  },
  {
    signalName: "sig_rsi_bb_position",
    widgetType: "signal_rsi_bb_position",
    label: "RSI BB Position",
    description: "RSI Bollinger Band position",
  },
  {
    signalName: "sig_bullish_divergence",
    widgetType: "signal_bullish_divergence",
    label: "Bullish Divergence",
    description: "Bullish price-indicator divergence",
  },
  {
    signalName: "sig_bearish_divergence",
    widgetType: "signal_bearish_divergence",
    label: "Bearish Divergence",
    description: "Bearish price-indicator divergence",
  },
  {
    signalName: "sig_ou_theta",
    widgetType: "signal_ou_theta",
    label: "OU Theta",
    description: "Ornstein-Uhlenbeck mean-reversion speed",
  },
  {
    signalName: "sig_half_life_quality",
    widgetType: "signal_half_life_quality",
    label: "Half-Life Quality",
    description: "Mean-reversion half-life quality",
  },
  {
    signalName: "sig_adaptive_zscore",
    widgetType: "signal_adaptive_zscore",
    label: "Adaptive Z-Score",
    description: "Regime-adaptive z-score",
  },
  {
    signalName: "sig_zero_crossings",
    widgetType: "signal_zero_crossings",
    label: "Zero Crossings",
    description: "Signal zero-crossing frequency",
  },
  {
    signalName: "sig_macd_hist",
    widgetType: "signal_macd_hist",
    label: "MACD Histogram",
    description: "MACD histogram signal",
  },
  {
    signalName: "sig_stochrsi_k",
    widgetType: "signal_stochrsi_k",
    label: "StochRSI K",
    description: "Stochastic RSI %K line",
  },
  {
    signalName: "sig_regime_strength",
    widgetType: "signal_regime_strength",
    label: "Regime Strength",
    description: "Current regime strength",
  },
  {
    signalName: "sig_beta_deviation",
    widgetType: "signal_beta_deviation",
    label: "Beta Deviation",
    description: "Beta deviation from equilibrium",
  },
  {
    signalName: "sig_annualized_basis",
    widgetType: "signal_annualized_basis",
    label: "Annualized Basis",
    description: "Annualized futures basis",
  },
  // {
  //   signalName: "sig_basis_zscore",
  //   widgetType: "signal_basis_zscore",
  //   label: "Basis Z-Score",
  //   description: "Futures basis z-score",
  // },
  {
    signalName: "sig_lead_lag",
    widgetType: "signal_lead_lag",
    label: "Lead/Lag",
    description: "Lead-lag relationship signal",
  },
  {
    signalName: "sig_basis_convergence",
    widgetType: "signal_basis_convergence",
    label: "Basis Convergence",
    description: "Futures basis convergence rate",
  },
  {
    signalName: "sig_basis_to_funding",
    widgetType: "signal_basis_to_funding",
    label: "Basis/Funding",
    description: "Basis to funding rate ratio",
  },

  // ── Pairs Trading Raw Signals ──────────────────────────────────────────────
  {
    signalName: "close_x",
    widgetType: "pairs_close_x",
    label: "Close X",
    description: "Closing price of asset X",
  },
  {
    signalName: "close_y",
    widgetType: "pairs_close_y",
    label: "Close Y",
    description: "Closing price of asset Y",
  },
  {
    signalName: "ratio",
    widgetType: "pairs_ratio",
    label: "Price Ratio",
    description: "Raw price ratio (X/Y)",
  },
  {
    signalName: "z_score",
    widgetType: "pairs_z_score",
    label: "Z-Score",
    description: "Standardized spread z-score",
  },
  {
    signalName: "rolling_beta",
    widgetType: "pairs_rolling_beta",
    label: "Rolling Beta",
    description: "Rolling beta coefficient",
  },
  {
    signalName: "rolling_corr",
    widgetType: "pairs_rolling_corr",
    label: "Rolling Correlation",
    description: "Rolling correlation coefficient",
  },
  {
    signalName: "hurst",
    widgetType: "pairs_hurst",
    label: "Hurst Exponent",
    description: "Mean-reversion vs trending measure",
  },
  {
    signalName: "ratio_rsi",
    widgetType: "pairs_ratio_rsi",
    label: "Ratio RSI",
    description: "RSI of the price ratio",
  },
  {
    signalName: "rsi_bb_lower",
    widgetType: "pairs_rsi_bb_lower",
    label: "RSI BB Lower",
    description: "RSI Bollinger Band lower bound",
  },
  {
    signalName: "rsi_bb_mid",
    widgetType: "pairs_rsi_bb_mid",
    label: "RSI BB Mid",
    description: "RSI Bollinger Band midline",
  },
  {
    signalName: "rsi_bb_upper",
    widgetType: "pairs_rsi_bb_upper",
    label: "RSI BB Upper",
    description: "RSI Bollinger Band upper bound",
  },
  {
    signalName: "theta",
    widgetType: "pairs_theta",
    label: "OU Theta",
    description: "Ornstein-Uhlenbeck mean-reversion speed",
  },
  {
    signalName: "half_life",
    widgetType: "pairs_half_life",
    label: "Half Life",
    description: "Mean-reversion half-life in days",
  },
  {
    signalName: "adaptive_z",
    widgetType: "pairs_adaptive_z",
    label: "Adaptive Z-Score",
    description: "Regime-adaptive z-score",
  },
  {
    signalName: "macd_hist",
    widgetType: "pairs_macd_hist",
    label: "MACD Histogram",
    description: "MACD histogram",
  },
  {
    signalName: "stochrsi_k",
    widgetType: "pairs_stochrsi_k",
    label: "StochRSI K",
    description: "Stochastic RSI %K line",
  },
  {
    signalName: "funding_rate_x",
    widgetType: "pairs_funding_rate_x",
    label: "Funding Rate X",
    description: "Funding rate of asset X",
  },
  {
    signalName: "funding_rate_y",
    widgetType: "pairs_funding_rate_y",
    label: "Funding Rate Y",
    description: "Funding rate of asset Y",
  },
  {
    signalName: "funding_divergence",
    widgetType: "pairs_funding_divergence",
    label: "Funding Divergence",
    description: "Divergence between funding rates X and Y",
  },
  {
    signalName: "basis_spread",
    widgetType: "pairs_basis_spread",
    label: "Basis Spread",
    description: "Futures basis spread",
  },
];

// O(1) lookup: widgetType → SignalDefinition
export const SIGNAL_MAP = new Map<string, SignalDefinition>(
  SIGNAL_DEFINITIONS.map((d) => [d.widgetType, d]),
);
